import { useState } from "react";
import { BookingPackageCN } from "@/types/booking-cn";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { X } from "lucide-react";

interface PackageCardCNProps {
  package: BookingPackageCN;
  onBookNow?: (packageId: string) => void;
}

const PackageCardCN = ({ package: pkg, onBookNow }: PackageCardCNProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const getOverlayColor = (id: string) => {
    switch (id) {
      case 'classic': return 'bg-green-900/70';
      case 'royal': return 'bg-red-900/70';
      case 'majestic': return 'bg-blue-900/60';
      case 'twilight': return 'bg-orange-900/60';
      default: return 'bg-black/60';
    }
  };

  return (
    <>
      <div className="relative h-[400px] overflow-hidden rounded-lg group">
        <img
          src={pkg.image}
          alt={pkg.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className={`absolute inset-0 ${getOverlayColor(pkg.id)} transition-opacity`} />
        
        <div className="absolute inset-0 p-6 flex flex-col justify-between text-white">
          <div>
            <div className="inline-block border-2 border-white px-4 py-2 mb-4">
              <h3 className="text-2xl font-bold">{pkg.name}</h3>
            </div>
          </div>
          
          <div>
            <ul className="space-y-2 mb-3 text-sm">
              {pkg.features.slice(0, 3).map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            {pkg.features.length > 3 && (
              <button
                onClick={() => setIsDialogOpen(true)}
                className="text-white text-sm underline mb-4 hover:text-white/80 transition-colors"
              >
                查看更多
              </button>
            )}
            
            <Button 
              variant="outline" 
              className="w-full bg-white text-foreground hover:bg-white/90 border-white"
              onClick={() => onBookNow?.(pkg.id)}
            >
              立即预订 - AED {pkg.price}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">关闭</span>
          </DialogClose>
          
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{pkg.name}</DialogTitle>
            <p className="text-xl font-semibold text-primary mt-2">AED {pkg.price}</p>
          </DialogHeader>
          
          <div className="mt-4">
            <h4 className="font-semibold mb-3 text-lg">套餐包含：</h4>
            <ul className="space-y-3">
              {pkg.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PackageCardCN;
